// pages/approvals/PaymentRequestApprovalPage.tsx
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, DollarSign, FileText, Clock, Building, Calendar, User } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { usePaymentRequest, useApprovePaymentRequest, useRejectPaymentRequest } from '@/hooks/finance/usePaymentRequests';
import DocumentList from '@/components/finance/DocumentList';
import { useCanApproveRequest } from '@/hooks/finance/useCanApproveRequest';
import { PaymentRequestStatus } from '@/types/database/payment-requests';
import { format } from 'date-fns';
import { PaymentRequestAllocationsBadges } from '@/components/finance/PaymentRequestAllocationsBadges';

const statusConfig: Record<PaymentRequestStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <FileText className="h-4 w-4" /> },
  pending_approval: { label: 'Pending Approval', variant: 'outline', icon: <Clock className="h-4 w-4" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="h-4 w-4" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-4 w-4" /> },
  paid: { label: 'Paid', variant: 'default', icon: <DollarSign className="h-4 w-4" /> },
  cancelled: { label: 'Cancelled', variant: 'secondary', icon: <XCircle className="h-4 w-4" /> },
};

const PaymentRequestApprovalPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: request, isLoading } = usePaymentRequest(id || '');
  const approveRequest = useApprovePaymentRequest();
  const rejectRequest = useRejectPaymentRequest();
  const { canApprove, reason: approvalBlockReason, isLoading: approverLoading } = useCanApproveRequest(request || null);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const formatCurrency = (amount: number, currency?: string) => {
    const code = currency || request?.currency || 'ETB';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
    } catch {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} ${code}`;
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    await approveRequest.mutateAsync({ id, comments });
    setApproveDialogOpen(false);
    setComments('');
    navigate('/my-approvals');
  };

  const handleReject = async () => {
    if (!id || !rejectionReason) return;
    await rejectRequest.mutateAsync({ id, reason: rejectionReason, comments });
    setRejectDialogOpen(false);
    setRejectionReason('');
    setComments('');
    navigate('/my-approvals');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Request not found</h2>
          <Button className="mt-4" onClick={() => navigate('/my-approvals')}>
            Back to Approvals
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/my-approvals')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{request.request_number}</h1>
                <Badge variant={statusConfig[request.status].variant}>
                  {statusConfig[request.status].icon}
                  <span className="ml-1">{statusConfig[request.status].label}</span>
                </Badge>
              </div>
              <p className="text-muted-foreground">{request.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canApprove && request?.status === 'pending_approval' && (
              <>
                <Button variant="destructive" onClick={() => setRejectDialogOpen(true)}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => setApproveDialogOpen(true)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {!canApprove && request?.status === 'pending_approval' && !approverLoading && (
              <Badge variant="outline" className="text-muted-foreground">
                {approvalBlockReason || 'Awaiting approval from designated approver'}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {request.description && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                  <p className="mt-1">{request.description}</p>
                </div>
              )}

              <Separator />

              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Line Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">Description</th>
                        <th className="text-right p-3">Qty</th>
                        <th className="text-right p-3">Unit Price</th>
                        <th className="text-right p-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {request.items?.map((item) => (
                        <tr key={item.id} className="border-t">
                          <td className="p-3">{item.description}</td>
                          <td className="text-right p-3">{item.quantity}</td>
                          <td className="text-right p-3">{formatCurrency(item.unit_price)}</td>
                          <td className="text-right p-3">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50">
                      <tr className="border-t">
                        <td colSpan={3} className="p-3 text-right font-medium">Subtotal</td>
                        <td className="p-3 text-right">{formatCurrency(request.amount)}</td>
                      </tr>
                      {request.vat_applicable && (
                        <tr>
                          <td colSpan={3} className="p-3 text-right font-medium">VAT (15%)</td>
                          <td className="p-3 text-right">{formatCurrency(request.vat_amount)}</td>
                        </tr>
                      )}
                      <tr className="font-semibold">
                        <td colSpan={3} className="p-3 text-right">Total</td>
                        <td className="p-3 text-right">{formatCurrency(request.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Requestor</p>
                    <p className="font-medium">
                      {request.requester_first_name && request.requester_last_name
                        ? `${request.requester_first_name} ${request.requester_last_name}`
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{request.department?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{request.category?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Preferred Payment Date</p>
                    <p className="font-medium">
                      {request.preferred_payment_date
                        ? format(new Date(request.preferred_payment_date), 'MMM d, yyyy')
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(request.total_amount)}</p>
                </div>
                <Separator />
                <PaymentRequestAllocationsBadges
                  paymentRequestId={request.id}
                  fallbackBusinessUnit={(request as any).business_unit}
                />
              </CardContent>
            </Card>

            {request.vendor_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Vendor Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{request.vendor_name}</p>
                  {request.vendor_bank_name && (
                    <div className="text-sm text-muted-foreground">
                      <p>{request.vendor_bank_name}</p>
                      <p>A/C: {request.vendor_bank_account}</p>
                      {request.vendor_bank_branch && <p>Branch: {request.vendor_bank_branch}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {request.documents && request.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Supporting Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <DocumentList
                    documents={request.documents}
                    bucketName="finance-documents"
                    canDelete={false}
                  />
                </CardContent>
              </Card>
            )}

            {request.approvals && request.approvals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Approval History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {request.approvals.map((approval) => (
                      <div key={approval.id} className="flex items-start gap-3">
                        {approval.status === 'approved' ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive mt-1" />
                        )}
                        <div>
                          <p className="text-sm font-medium">
                            Level {approval.approval_level} - {approval.status}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(approval.approved_at), 'MMM d, yyyy HH:mm')}
                          </p>
                          {approval.comments && (
                            <p className="text-sm mt-1">{approval.comments}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Payment Request</DialogTitle>
            <DialogDescription>
              You are about to approve this payment request for {formatCurrency(request.total_amount)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Comments (optional)</Label>
              <Textarea
                placeholder="Add any comments..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={approveRequest.isPending}>
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                placeholder="Enter the reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectionReason || rejectRequest.isPending}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PaymentRequestApprovalPage;
